import React, { useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  getDistrictListforEvent,
  getBlockList,
  getGpList,
} from "../../Service/CashAnalysisService";
import "react-toastify/dist/ReactToastify.css";

const GeneralBalance = () => {
  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  // Dates
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(getCurrentDate());

  // User
  const jsonString = sessionStorage.getItem("SAHAJ_SARAL_USER");
  const userData = JSON.parse(jsonString || "{}");

  // Location
  const [district, setDistrict] = useState("");
  const [block, setBlock] = useState("");
  const [gp, setGp] = useState("");

  const [getDistrictDataList, setDistrictDataList] = useState([]);
  const [getBlockDataList, setBlockDataList] = useState([]);
  const [getGpDataList, setGpDataList] = useState([]);

  // Report
  const [reportType, setReportType] = useState("");

  // GL Group
  const [glGroupList, setGlGroupList] = useState([]);
  const [glGroup, setGlGroup] = useState("");

  // Data
  const [glBalanceDtls, setGlBalanceDtls] = useState([]);

  // Loaders
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const printRef = useRef();

  const formatMoney = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "0.00";
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const totals = glBalanceDtls.reduce(
    (acc, item) => {
      acc.openingBalance += parseFloat(item.openingBalance) || 0;
      acc.receiptAmount += parseFloat(item.receiptAmount) || 0;
      acc.paymentAmount += parseFloat(item.paymentAmount) || 0;
      acc.closingBalance += parseFloat(item.closingBalance) || 0;
      return acc;
    },
    { openingBalance: 0, receiptAmount: 0, paymentAmount: 0, closingBalance: 0 }
  );

  // ---------- API: GL Group ----------
  const fetchGlGroupList = async () => {
    try {
      setLoadingGroups(true);
      const res = await fetch(
        "https://javaapi.wbpms.in/api/MIS/GlGroupForMISReport"
      );
      if (!res.ok) throw new Error("Failed to fetch GL Group list");
      const data = await res.json();
      setGlGroupList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("GL Group list fetch failed");
      setGlGroupList([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // ---------- Search ----------
  const onSearch = async () => {
    if (!district || district === "0") return toast.error("Please select a District");
    if (!glGroup) return toast.error("Please select a GL Group");
    if (!fromDate) return toast.error("Please select a From Date");
    if (!toDate) return toast.error("Please select a To Date");
    if (!reportType) return toast.error("Please select a Report Type");

    const distLgd = district || 0;
    const blockLgd = block && block !== "0" ? block : 0;
    const gpLgd = gp && gp !== "0" ? gp : 0;

    const url =
      `https://javaapi.wbpms.in/api/MIS/GlBalance` +
      `?distLgd=${encodeURIComponent(distLgd)}` +
      `&blockLgd=${encodeURIComponent(blockLgd)}` +
      `&gpLgd=${encodeURIComponent(gpLgd)}` +
      `&glGroup=${encodeURIComponent(glGroup)}` +
      `&frmDate=${encodeURIComponent(fromDate)}` +
      `&toDate=${encodeURIComponent(toDate)}` +
      `&rptType=${encodeURIComponent(reportType)}`;

    try {
      setLoadingReport(true);
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch GL Balance");
      const data = await res.json();

      const rows = Array.isArray(data?.dtls) ? data.dtls : [];
      setGlBalanceDtls(rows);
      if (!rows.length) toast.info("No data found");
    } catch (err) {
      console.error(err);
      toast.error("GL Balance fetch failed");
      setGlBalanceDtls([]);
    } finally {
      setLoadingReport(false);
    }
  };

  // ---------- Excel Export ----------
  const exportToExcel = () => {
    if (!glBalanceDtls?.length) {
      toast.error("No data to export");
      return;
    }

    const rows = glBalanceDtls.map((r, i) => ({
      SL: i + 1,
      "PRI Name": r.priName,
      "LGD Code": r.lgdCode,
      "Opening Balance": Number(r.openingBalance || 0),
      "Receipt Amount": Number(r.receiptAmount || 0),
      "Payment Amount": Number(r.paymentAmount || 0),
      "Closing Balance": Number(r.closingBalance || 0),
    }));

    rows.push({
      SL: "",
      "PRI Name": "TOTAL",
      "LGD Code": "",
      "Opening Balance": totals.openingBalance,
      "Receipt Amount": totals.receiptAmount,
      "Payment Amount": totals.paymentAmount,
      "Closing Balance": totals.closingBalance,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 5 },
      { wch: 40 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GL Balance");

    const fileName = `GL_Balance_${district}_${block || 0}_${gp || 0}_${glGroup}_${fromDate}_${toDate}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([wbout], { type: "application/octet-stream" }), fileName);
  };

  // ---------- Events ----------
  const onFromDate = (e) => setFromDate(e.target.value);

  const onReportType = (e) => {
    setReportType(e.target.value);
    setGlBalanceDtls([]);
  };

  const onDistrict = (e) => {
    const v = e.target.value;
    setDistrict(v);
    setBlock("");
    setGp("");
    setGlBalanceDtls([]);

    getBlockList(v, 0).then((result) => {
      setBlockDataList(result?.data || []);
    });
    setGpDataList([]);
  };

  const onBlock = (e) => {
    const v = e.target.value;
    setBlock(v);
    setGp("");
    setGlBalanceDtls([]);

    getGpList(district || userData?.DIST_LGD, v, 0).then((result) => {
      setGpDataList(result?.data || []);
    });
  };

  const onGp = (e) => {
    setGp(e.target.value);
    setGlBalanceDtls([]);
  };

  // ---------- Initial Load ----------
  useEffect(() => {
    const jsonString = sessionStorage.getItem("SAHAJ_SARAL_USER");
    const data = JSON.parse(jsonString || "{}");

    // District list
    getDistrictListforEvent(
      data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
        ? data?.DIST_LGD
        : 0
    ).then((result) => {
      setDistrictDataList(result?.data || []);
      setDistrict(
        data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
          ? String(data?.DIST_LGD || "0")
          : ""
      );
    });

    // Block list
    getBlockList(
      data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
        ? data?.DIST_LGD
        : 0,
      data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
        ? data?.BLOCK_LGD
        : 0
    ).then((result) => {
      setBlockDataList(result?.data || []);
      setBlock(
        data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
          ? String(data?.BLOCK_LGD || "0")
          : ""
      );
    });

    // GP list
    getGpList(
      data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
        ? data?.DIST_LGD
        : 0,
      data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
        ? data?.BLOCK_LGD
        : 0,
      data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
        ? data?.GP_LGD
        : 0
    ).then((result) => {
      setGpDataList(result?.data || []);
      setGp(
        data?.USER_LEVEL === "DIST" || data?.USER_LEVEL === "BLOCK" || data?.USER_LEVEL === "GP"
          ? String(data?.GP_LGD || "0")
          : ""
      );
    });

    // GL Group list
    fetchGlGroupList();
  }, []);

  return (
    <>
      <ToastContainer />

      <div className="bg-white rounded-lg p-2 flex flex-col flex-grow" style={{ marginTop: "-40px" }}>
        <legend className="text-lg font-semibold text-cyan-700">General Ledger Balance</legend>

        {/* FILTER GRID (aligned) */}
        <div className="py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 items-end">
            {/* District */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">District</label>
              <select
                className="text-sm w-full h-9 px-2 border border-gray-300 rounded-md"
                value={district}
                onChange={onDistrict}
              >
                {(userData?.USER_LEVEL === "DIST" ||
                  userData?.USER_LEVEL === "BLOCK" ||
                  userData?.USER_LEVEL === "GP") ? null : (
                  <option value="0">Select District</option>
                )}

                {getDistrictDataList.map((distRow, index) => (
                  <option key={index} value={distRow.DistLgd}>
                    {distRow.DistName}
                  </option>
                ))}
              </select>
            </div>

            {/* Block */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">Block</label>
              <select
                className="text-sm w-full h-9 px-2 border border-gray-300 rounded-md"
                value={block}
                onChange={onBlock}
              >
                {(userData?.USER_LEVEL === "BLOCK" || userData?.USER_LEVEL === "GP") ? null : (
                  <option value="0">Select Block</option>
                )}

                {getBlockDataList.map((blRow, index) => (
                  <option key={index} value={blRow.BlockLgd ?? blRow.BLOCK_LGD}>
                    {blRow.BlockName ?? blRow.BLOCK_NAME}
                  </option>
                ))}
              </select>
            </div>

            {/* GP */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">Gram Panchayat</label>
              <select
                className="text-sm w-full h-9 px-2 border border-gray-300 rounded-md"
                value={gp}
                onChange={onGp}
              >
                {userData?.USER_LEVEL === "GP" ? null : <option value="0">Select Gram Panchayat</option>}

                {getGpDataList.map((gpRow, index) => (
                  <option key={index} value={gpRow.GPLgd ?? gpRow.GP_LGD}>
                    {gpRow.GPName ?? gpRow.GP_NAME}
                  </option>
                ))}
              </select>
            </div>

            {/* GL Group */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">
                GL Group <span className="text-red-500">*</span>
                {loadingGroups && <span className="ml-2 text-xs text-blue-600">Loading...</span>}
              </label>
              <select
                className="text-sm w-full h-9 px-2 border border-gray-300 rounded-md"
                value={glGroup}
                onChange={(e) => {
                  setGlGroup(e.target.value);
                  setGlBalanceDtls([]);
                }}
              >
                <option value="">Select GL Group</option>
                {glGroupList.map((g, idx) => (
                  <option key={idx} value={g.groupId}>
                    {g.groupDesc} ({g.groupId})
                  </option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">
                From Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="text-sm w-full h-9 px-2 border border-gray-300 rounded-md"
                value={fromDate}
                onChange={onFromDate}
              />
            </div>

            {/* To Date */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">
                To Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="text-sm w-full h-9 px-2 border border-gray-300 rounded-md"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            {/* Report Type */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700">
                Report Type <span className="text-red-500">*</span>
              </label>
              <select
                className="text-sm w-full h-9 px-2 border border-gray-300 rounded-md"
                value={reportType}
                onChange={onReportType}
              >
                <option value="">Select Report For</option>
                <option value="1">ZP</option>
                <option value="2">PS</option>
                <option value="3">GP</option>
              </select>
            </div>

            {/* Search */}
            <div className="w-full">
              <button
                type="button"
                disabled={loadingReport}
                onClick={onSearch}
                className={`w-full h-9 rounded-md text-white text-sm font-medium
                  ${loadingReport ? "bg-cyan-600/60 cursor-not-allowed" : "bg-cyan-700 hover:bg-cyan-800"}
                `}
              >
                {loadingReport ? "Searching..." : "Search"}
              </button>
            </div>

            {/* Export */}
            <div className="w-full">
              <button
                type="button"
                disabled={!glBalanceDtls?.length}
                onClick={exportToExcel}
                className={`w-full h-9 rounded-md text-white text-sm font-medium
                  ${!glBalanceDtls?.length ? "bg-green-600/50 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}
                `}
              >
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div ref={printRef} className="mt-3">
          {glBalanceDtls?.length > 0 && (
            <div className="overflow-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2">SL</th>
                    <th className="border p-2">PRI Name</th>
                    <th className="border p-2">LGD Code</th>
                    <th className="border p-2 text-right">Opening Balance</th>
                    <th className="border p-2 text-right">Receipt Amount</th>
                    <th className="border p-2 text-right">Payment Amount</th>
                    <th className="border p-2 text-right">Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {glBalanceDtls.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="border p-2">{idx + 1}</td>
                      <td className="border p-2">{row.priName}</td>
                      <td className="border p-2">{row.lgdCode}</td>
                      <td className="border p-2 text-right">{formatMoney(row.openingBalance)}</td>
                      <td className="border p-2 text-right">{formatMoney(row.receiptAmount)}</td>
                      <td className="border p-2 text-right">{formatMoney(row.paymentAmount)}</td>
                      <td className="border p-2 text-right">{formatMoney(row.closingBalance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="border p-2" colSpan={3}>Total</td>
                    <td className="border p-2 text-right">{formatMoney(totals.openingBalance)}</td>
                    <td className="border p-2 text-right">{formatMoney(totals.receiptAmount)}</td>
                    <td className="border p-2 text-right">{formatMoney(totals.paymentAmount)}</td>
                    <td className="border p-2 text-right">{formatMoney(totals.closingBalance)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* FULLSCREEN LOADER */}
        {loadingReport && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
            <div className="bg-white px-4 py-3 rounded shadow text-sm font-semibold">
              Loading Report...
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GeneralBalance;
